import { acknowledged, conflictCopyName, linkFromRemote, needsPush, eolToWire, type CloudMeta } from '../core/cloud';
import type { CloudDocState } from '../core/cloud';
import { contentHash, type CloudLink } from '../core/documents';
import { createStore } from '../core/store';
import { CloudError, type CloudApi, type RemoteDoc, type RemoteMeta } from './api';

/**
 * The synchronisation engine.
 *
 * It moves documents between the editor and the cloud and decides nothing
 * about the editor: the host (the controller) owns text, tabs and local
 * persistence. The engine only ever reads a document's current state from the
 * host and reports outcomes back.
 *
 * Guarantees:
 * - One request per document at a time; edits made while it is in flight stay
 *   pending and go out in the next one. Responses therefore cannot arrive out
 *   of order for a document.
 * - A document counts as synced only after the server acknowledged exactly the
 *   state that was sent. The acknowledged state, not the current text, is what
 *   is recorded.
 * - Every write is a compare-and-swap on the document's revision. A rejected
 *   write never loses text: the local version is preserved as a conflict copy
 *   before the server's version replaces the open document.
 * - Retries back off exponentially; a lost response is recognised on retry
 *   (the server already holds the same text) and acknowledged, not reported as
 *   a conflict.
 */

export interface SyncDoc {
  meta: CloudMeta;
  text: string;
  /** The logical workspace the document currently belongs to (its window's), if any. */
  workspace: { id: string; name: string } | null;
  /** Position within its workspace. Carried along with other changes, never a reason to write by itself. */
  sortOrder: number;
}

export interface SyncHost {
  /** Documents that belong to a cloud library: open in a window, or kept after their tab closed. */
  docs(): SyncDoc[];
  doc(id: string): SyncDoc | null;
  /** Record what the server holds for the document. */
  link(id: string, link: CloudLink): void;
  /** The document is no longer in the cloud: it stays on this device as an ordinary document. */
  unlink(id: string): void;
  /**
   * Replace the document with the server's version. With `keepLocalMeta`, a name,
   * format or workspace that was changed here since the last acknowledged revision
   * stays as edited (and remains to be pushed); the others follow the server. With
   * `keepLocalText` the open text is left alone and only the acknowledged state follows the server.
   */
  adopt(id: string, remote: RemoteDoc, options: { keepLocalMeta: boolean; keepLocalText?: boolean }): void;
  /** Keep `text` as a new cloud-bound document named `name`; returns its id. Must persist before returning. */
  keepCopy(fromId: string, name: string, text: string): string;
  /** Close a clean document that was deleted elsewhere. */
  drop(id: string): void;
  notify(message: string, tone?: 'info' | 'error'): void;
}

export interface SyncOptions {
  api: CloudApi;
  host: SyncHost;
  account: string;
  /** Whether edits are uploaded on their own. Read at the moment of use. */
  autosave: () => boolean;
  /** The session ended or was refused: the owner should try to refresh it (and call `resume`). */
  onAuthLost?: () => void;
  now?: () => number;
  /** Quiet period after the last edit before an upload. */
  debounceMs?: number;
  /** An upload happens at least this often during continuous typing. */
  maxWaitMs?: number;
  /** Upper bound of the retry delay. */
  maxBackoffMs?: number;
  /** How often to look for changes made on other devices while idle. */
  pollMs?: number;
  random?: () => number;
}

export interface SyncState {
  account: string;
  access: 'unknown' | 'granted' | 'denied';
  online: boolean;
  /** The engine cannot proceed until the person acts. */
  blocked: 'auth' | 'denied' | null;
  library: RemoteMeta[] | null;
  libraryStatus: 'idle' | 'loading' | 'ready' | 'error';
  docs: Record<string, CloudDocState>;
  inflight: number;
  lastSyncedAt: number | null;
  /** The latest failure worth telling the person about. */
  error: string | null;
}

export interface SyncEngine {
  readonly state: ReturnType<typeof createStore<SyncState>>;
  /** Learn the account's access, then look at the library and push what is pending. */
  start(): Promise<void>;
  stop(): void;
  /** The document changed (text, name, workspace) or was newly linked. */
  touch(id: string): void;
  /** Push everything pending now and look for remote changes. Resolves when done or failed. */
  syncNow(): Promise<void>;
  /** Push one document now. */
  flush(id: string): Promise<void>;
  /** Push every pending document now (autosave turned on, the page is hiding), without looking at the library. */
  pushPending(): Promise<void>;
  /** Cloud Autosave was switched: start uploading what is pending, or stop the timers. */
  autosaveChanged(): void;
  /** Look at the library (and apply remote changes to clean open documents). */
  refresh(): Promise<void>;
  fetch(id: string): Promise<RemoteDoc | null>;
  /** Delete a document in the cloud; false if the server holds a newer revision than the caller's. */
  remove(id: string): Promise<'deleted' | 'newer' | 'failed'>;
  setOnline(online: boolean): void;
  /** The session was refreshed or the person signed in again. */
  resume(): void;
  /** Cancel a document's scheduled work (it was closed or left the library). */
  forget(id: string): void;
  /** True while anything is pending or in flight. */
  busy(): boolean;
}

interface Runtime {
  inflight: Promise<void> | null;
  rerun: boolean;
  failures: number;
  /** The latest failure of this document was a lost connection (not the server's decision). */
  lastWasNetwork: boolean;
  rejected: string | null;
  timer: ReturnType<typeof setTimeout> | undefined;
  /** When the current run of unsent edits began, for the max-wait bound. */
  dirtySince: number | null;
  /** Fingerprints of the texts this device recently sent, to recognise its own earlier writes coming back. */
  sentHashes: string[];
}

const ROUNDS = 4;

export function createSyncEngine(options: SyncOptions): SyncEngine {
  const { api, host, account } = options;
  const now = options.now ?? (() => Date.now());
  const random = options.random ?? Math.random;
  const debounceMs = options.debounceMs ?? 1500;
  const maxWaitMs = options.maxWaitMs ?? 10_000;
  const maxBackoffMs = options.maxBackoffMs ?? 60_000;
  const pollMs = options.pollMs ?? 60_000;

  const state = createStore<SyncState>({
    account,
    access: 'unknown',
    online: true,
    blocked: null,
    library: null,
    libraryStatus: 'idle',
    docs: {},
    inflight: 0,
    lastSyncedAt: null,
    error: null,
  });

  const runtimes = new Map<string, Runtime>();
  let stopped = false;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let refreshing: Promise<void> | null = null;

  const rt = (id: string): Runtime => {
    let r = runtimes.get(id);
    if (!r) {
      r = { inflight: null, rerun: false, failures: 0, lastWasNetwork: false, rejected: null, timer: undefined, dirtySince: null, sentHashes: [] };
      runtimes.set(id, r);
    }
    return r;
  };

  const mine = (): SyncDoc[] => host.docs().filter((d) => d.meta.cloud.account === account);
  const pending = (d: SyncDoc) => needsPush(d.meta, d.text, d.workspace?.id ?? null);

  // --- state ---------------------------------------------------------------

  function docState(d: SyncDoc): CloudDocState {
    const r = runtimes.get(d.meta.id);
    const s = state.get();
    if (!pending(d)) return d.meta.cloud.revision > 0 && !r?.inflight ? 'synced' : r?.inflight ? 'syncing' : 'pending';
    if (s.blocked || r?.rejected) return 'blocked';
    if (r?.inflight) return 'syncing';
    if (!s.online || r?.lastWasNetwork) return 'offline';
    if (r && r.failures > 0) return 'error';
    return 'pending';
  }

  function publish(patch: Partial<SyncState> = {}) {
    const docs: Record<string, CloudDocState> = {};
    for (const d of mine()) docs[d.meta.id] = docState(d);
    let inflight = 0;
    for (const r of runtimes.values()) if (r.inflight) inflight++;
    state.set((s) => ({ ...s, ...patch, docs, inflight }));
  }

  function recordSynced() {
    if (mine().some(pending)) return;
    publish({ lastSyncedAt: now(), error: null });
  }

  // --- scheduling ----------------------------------------------------------

  function backoff(failures: number): number {
    const base = Math.min(maxBackoffMs, 2000 * 2 ** Math.max(0, failures - 1));
    return Math.round(base * (0.75 + random() * 0.5));
  }

  function schedule(id: string, delay: number) {
    const r = rt(id);
    clearTimeout(r.timer);
    if (stopped) return;
    r.timer = setTimeout(() => {
      r.timer = undefined;
      // Timers are autosave's. Turned off meanwhile, the change simply stays pending.
      if (!options.autosave()) return publish();
      void run(id);
    }, delay);
  }

  /** Schedule an automatic upload after the quiet period, bounded by the max wait. */
  function scheduleEdit(id: string) {
    const r = rt(id);
    const t = now();
    r.dirtySince ??= t;
    const delay = Math.max(0, Math.min(debounceMs, r.dirtySince + maxWaitMs - t));
    schedule(id, delay);
  }

  function canTry(): boolean {
    const s = state.get();
    return !stopped && !s.blocked && s.online && s.access !== 'denied';
  }

  // --- one document --------------------------------------------------------

  function run(id: string): Promise<void> {
    const r = rt(id);
    if (r.inflight) {
      r.rerun = true;
      return r.inflight;
    }
    if (!canTry()) {
      publish();
      return Promise.resolve();
    }
    r.inflight = (async () => {
      try {
        for (let round = 0; round < ROUNDS && !stopped; round++) {
          r.rerun = false;
          const again = await cycle(id);
          if (!again && !r.rerun) break;
        }
        const left = host.doc(id);
        if (!stopped && left && pending(left) && r.failures === 0 && !r.rejected && options.autosave() && canTry()) schedule(id, debounceMs);
      } finally {
        r.inflight = null;
        publish();
        recordSynced();
      }
    })();
    publish();
    return r.inflight;
  }

  /** One push attempt. Returns true when the document should be looked at again straight away. */
  async function cycle(id: string): Promise<boolean> {
    if (stopped) return false;
    const doc = host.doc(id);
    if (!doc || doc.meta.cloud.account !== account || !pending(doc)) return false;
    const r = rt(id);
    const sent = { text: doc.text, name: doc.meta.name, kind: doc.meta.kind, format: { ...doc.meta.format }, workspaceId: doc.workspace?.id ?? null };
    r.sentHashes = [...r.sentHashes.slice(-7), contentHash(sent.text)];
    try {
      const result = await api.push({
        id,
        baseRevision: doc.meta.cloud.revision,
        name: sent.name,
        kind: sent.kind,
        content: sent.text,
        eol: eolToWire(sent.format.eol),
        bom: sent.format.bom,
        workspace: doc.workspace,
        sortOrder: doc.sortOrder,
      });
      r.failures = 0;
      r.lastWasNetwork = false;
      r.rejected = null;
      // The person may have unlinked, moved or closed the document while the request was out.
      const still = host.doc(id);
      if (!still || still.meta.cloud.account !== account) return false;
      switch (result.status) {
        case 'ok': {
          // Record exactly what was sent. Whatever was typed meanwhile is still pending.
          host.link(id, acknowledged(account, result.revision, sent));
          noteInLibrary(id, sent, result.revision, result.updated_at);
          if (host.doc(id) && pending(host.doc(id)!)) {
            r.dirtySince = now();
            if (options.autosave()) schedule(id, debounceMs);
          } else {
            r.dirtySince = null;
          }
          return false;
        }
        case 'conflict':
          return resolveConflict(id, sent, result.document);
        case 'deleted':
          goneFromCloud(id, true);
          return false;
        case 'not_found':
          // The row is not there at all (purged, or never existed here): nothing proves it was deleted on purpose.
          goneFromCloud(id, false);
          return false;
      }
    } catch (error) {
      fail(id, error);
      return false;
    }
  }

  /** `true` when the document should be pushed again (its metadata still differs from the server's). */
  function resolveConflict(id: string, sent: { text: string; name: string }, remote: RemoteDoc): boolean {
    const doc = host.doc(id);
    if (!doc) return false;
    const current = doc.text;
    const base = doc.meta.cloud;

    // The server already holds the text we sent (the first response was lost) or the text we have now.
    if (remote.content === sent.text || remote.content === current) {
      host.link(id, linkFromRemote(account, remote));
      return pending(host.doc(id) ?? doc);
    }

    // Only name, format or workspace changed here: the server's text is simply newer.
    if (contentHash(current) === base.hash && contentHash(sent.text) === base.hash) {
      host.adopt(id, remote, { keepLocalMeta: true });
      return true;
    }

    // The server's text is nothing new to this device: either what it held when we last synced (another
    // device only renamed or moved the document) or something this device itself sent earlier whose
    // acknowledgement was lost. Our text is the newest; follow the server's revision and send it again.
    const theirs = contentHash(remote.content);
    if (theirs === base.hash || rt(id).sentHashes.includes(theirs)) {
      host.adopt(id, remote, { keepLocalMeta: true, keepLocalText: true });
      return true;
    }

    // Both sides changed the text. Keep this device's words as their own document first,
    // then let the open document follow the server. Nothing is overwritten on either side.
    const copyName = conflictCopyName(doc.meta.name, new Date(now()), host.docs().map((d) => d.meta.name));
    const copyId = host.keepCopy(id, copyName, current);
    host.adopt(id, remote, { keepLocalMeta: false });
    host.notify(`“${doc.meta.name}” was also changed elsewhere. Your version is kept as “${copyName}”.`, 'info');
    touch(copyId);
    return false;
  }

  /**
   * The cloud no longer has this document. Only a tombstone proves it was deleted deliberately; then a copy
   * that holds nothing unique is closed. Otherwise (words only this device has, or no proof of deletion)
   * the document stays, as an ordinary local one.
   */
  function goneFromCloud(id: string, tombstoned: boolean) {
    if (stopped) return;
    const doc = host.doc(id);
    if (!doc) return;
    const r = rt(id);
    clearTimeout(r.timer);
    // `link.hash` is what the cloud last held; if the text still equals it, nothing here is unique.
    const clean = contentHash(doc.text) === doc.meta.cloud.hash && doc.meta.cloud.revision > 0;
    if (clean && tombstoned) {
      host.notify(`“${doc.meta.name}” was deleted on another device.`);
      host.drop(id);
    } else {
      host.unlink(id);
      host.notify(
        tombstoned
          ? `“${doc.meta.name}” was deleted on another device. Your changes are kept here as a local document.`
          : `“${doc.meta.name}” is no longer in your cloud library. It is kept here as a local document.`,
      );
    }
    forget(id);
  }

  function fail(id: string, error: unknown) {
    if (stopped) return;
    const r = rt(id);
    const e = error instanceof CloudError ? error : new CloudError('server', 'MyTXT cloud failed unexpectedly.');
    switch (e.kind) {
      case 'network':
        r.failures++;
        r.lastWasNetwork = true;
        if (options.autosave()) schedule(id, backoff(r.failures));
        publish();
        return;
      case 'server':
        r.failures++;
        r.lastWasNetwork = false;
        if (options.autosave()) schedule(id, backoff(r.failures));
        publish({ error: e.message });
        return;
      case 'auth':
        publish({ blocked: 'auth', error: e.message });
        options.onAuthLost?.();
        return;
      case 'denied':
        publish({ blocked: 'denied', access: 'denied', error: e.message });
        return;
      case 'rejected':
        r.rejected = e.message;
        publish({ error: `“${host.doc(id)?.meta.name ?? 'A document'}” can’t be synced: ${e.message}` });
        return;
    }
  }

  // --- library -------------------------------------------------------------

  function noteInLibrary(id: string, sent: { name: string; kind: RemoteMeta['kind']; format: { eol: string; bom: boolean }; workspaceId: string | null }, revision: number, updatedAt: string) {
    const library = state.get().library;
    if (!library) return;
    const entry: RemoteMeta = {
      id,
      name: sent.name,
      kind: sent.kind,
      revision,
      updated_at: updatedAt,
      deleted_at: null,
      workspace_id: sent.workspaceId,
      sort_order: host.doc(id)?.sortOrder ?? 0,
      eol: sent.format.eol === '\r\n' ? 'crlf' : 'lf',
      has_bom: sent.format.bom,
    };
    state.set((s) => ({ ...s, library: [entry, ...(s.library ?? []).filter((x) => x.id !== id)] }));
  }

  async function refresh(): Promise<void> {
    if (refreshing) return refreshing;
    if (!canTry()) return;
    refreshing = (async () => {
      publish({ libraryStatus: state.get().library ? state.get().libraryStatus : 'loading' });
      try {
        // What each document's revision was when the question was asked: an answer about an older state says nothing about a newer one.
        const asked = new Map(mine().map((d) => [d.meta.id, d.meta.cloud.revision]));
        const rows = await api.list();
        if (stopped) return;
        const byId = new Map(rows.map((r) => [r.id, r]));
        publish({ library: rows.filter((r) => !r.deleted_at), libraryStatus: 'ready' });
        // A listing that lacks a document we know of is not proof it is gone: access may have been withdrawn
        // (row-level security then returns nothing, quietly). Confirm access before drawing any conclusion.
        if (mine().some((d) => d.meta.cloud.revision > 0 && !byId.has(d.meta.id))) {
          if (!(await api.access())) {
            if (!stopped) publish({ access: 'denied', blocked: 'denied', error: 'This account is not enabled for MyTXT cloud storage.' });
            return;
          }
        }
        for (const doc of mine()) {
          if (stopped) return;
          if (asked.get(doc.meta.id) !== doc.meta.cloud.revision) continue;
          await reconcile(doc, byId.get(doc.meta.id));
        }
        recordSynced();
      } catch (error) {
        if (error instanceof CloudError && error.kind === 'auth') {
          publish({ blocked: 'auth', error: error.message, libraryStatus: 'error' });
          options.onAuthLost?.();
        } else if (error instanceof CloudError && error.kind === 'denied') {
          publish({ blocked: 'denied', access: 'denied', error: error.message, libraryStatus: 'error' });
        } else {
          publish({ libraryStatus: 'error', ...(error instanceof CloudError && error.kind !== 'network' ? { error: error.message } : {}) });
        }
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  /** Bring one local document in line with what the listing says about it. */
  async function reconcile(doc: SyncDoc, remote: RemoteMeta | undefined) {
    const id = doc.meta.id;
    const link = doc.meta.cloud;
    if (link.revision === 0) return; // not created yet; the push decides
    if (rt(id).inflight) return;
    if (!remote) {
      // Absent from the listing: ask for it by name (listings can be incomplete) before believing it is gone.
      const one = await api.fetch(id);
      const latest = host.doc(id);
      if (stopped || !latest || rt(id).inflight || latest.meta.cloud.revision !== link.revision) return;
      if (!one) return goneFromCloud(id, false);
      remote = one;
    }
    if (remote.deleted_at) return goneFromCloud(id, true);
    if (remote.revision <= link.revision) return;
    // The server has a newer revision. A clean document simply follows it; a document with
    // unsent changes meets the newer revision as a conflict when it is pushed.
    if (pending(doc)) {
      if (options.autosave()) schedule(id, 0);
      return;
    }
    if (rt(id).inflight) return;
    const full = await api.fetch(id);
    const latest = host.doc(id);
    // Re-check after the await: the person may have typed, or a push may have landed.
    if (stopped || !full || !latest || full.deleted_at || full.revision <= latest.meta.cloud.revision || pending(latest) || rt(id).inflight) return;
    host.adopt(id, full, { keepLocalMeta: false });
    publish();
  }

  // --- public --------------------------------------------------------------

  function touch(id: string) {
    const r = rt(id);
    // Failing and backing off: keystrokes must not turn the wait into a request per keystroke.
    if (r.failures > 0 && r.timer) return;
    // Typing: an upload is already scheduled for this burst. Push its quiet period out; nothing to recompute per keystroke.
    if (r.timer && !r.rejected && options.autosave()) return scheduleEdit(id);
    const doc = host.doc(id);
    if (!doc || doc.meta.cloud.account !== account) return;
    if (r.rejected) r.rejected = null;
    if (!pending(doc)) {
      r.dirtySince = null;
      clearTimeout(r.timer);
      publish();
      return;
    }
    if (options.autosave() && canTry()) scheduleEdit(id);
    publish();
  }

  async function syncNow() {
    if (stopped) return;
    if (state.get().blocked === 'denied') {
      // An administrator may have added the address since: look again before refusing.
      try {
        if (!(await api.access())) return;
        publish({ access: 'granted', blocked: null, error: null });
      } catch {
        return;
      }
    }
    const s = state.get();
    // A manual sync starts afresh: forget back-off and earlier rejections that may have been fixed.
    for (const r of runtimes.values()) {
      r.failures = 0;
      clearTimeout(r.timer);
    }
    if (s.blocked === 'auth') {
      options.onAuthLost?.();
      return;
    }
    if (!state.get().online) {
      publish();
      return;
    }
    await Promise.all(mine().filter(pending).map((d) => run(d.meta.id)));
    await refresh();
    // Pushes that came out of a conflict (copies) or follow-ups.
    await Promise.all(mine().filter(pending).map((d) => run(d.meta.id)));
  }

  function armPoll() {
    clearTimeout(pollTimer);
    if (stopped) return;
    pollTimer = setTimeout(() => {
      void (async () => {
        if (state.get().blocked === 'auth' && state.get().online) options.onAuthLost?.();
        else if (canTry()) await refresh();
        armPoll();
      })();
    }, pollMs);
  }

  return {
    state,

    async start() {
      stopped = false;
      try {
        const ok = await api.access();
        if (stopped) return;
        if (!ok) {
          publish({ access: 'denied', blocked: 'denied', error: 'This account is not enabled for MyTXT cloud storage.' });
          return;
        }
        publish({ access: 'granted', blocked: null, error: null });
      } catch (error) {
        if (error instanceof CloudError && error.kind === 'auth') {
          publish({ blocked: 'auth', error: error.message });
          options.onAuthLost?.();
          return;
        }
        // Offline at launch is normal: assume access (the server decides on the first write).
        publish({ access: state.get().access });
      }
      armPoll();
      if (options.autosave()) {
        await Promise.all(mine().filter(pending).map((d) => run(d.meta.id)));
      }
      await refresh();
    },

    stop() {
      stopped = true;
      clearTimeout(pollTimer);
      for (const r of runtimes.values()) clearTimeout(r.timer);
    },

    touch,
    syncNow,

    async flush(id) {
      const doc = host.doc(id);
      if (!doc || !pending(doc) || !canTry()) return;
      clearTimeout(rt(id).timer);
      await run(id);
    },

    async pushPending() {
      // With Cloud Autosave off nothing leaves on its own — not even when the page is hidden.
      if (!canTry() || !options.autosave()) return;
      for (const r of runtimes.values()) clearTimeout(r.timer);
      await Promise.all(mine().filter(pending).map((d) => run(d.meta.id)));
    },

    autosaveChanged() {
      if (options.autosave()) void this.pushPending();
      else for (const r of runtimes.values()) clearTimeout(r.timer);
      publish();
    },

    refresh,

    async fetch(id) {
      return api.fetch(id);
    },

    async remove(id) {
      const doc = host.doc(id);
      const known = doc?.meta.cloud.revision ?? state.get().library?.find((l) => l.id === id)?.revision ?? 0;
      try {
        const result = await api.remove(id, known);
        if (result.status === 'conflict') return 'newer';
        state.set((s) => ({ ...s, library: (s.library ?? []).filter((x) => x.id !== id) }));
        forget(id);
        return 'deleted';
      } catch (error) {
        if (error instanceof CloudError && error.kind === 'auth') options.onAuthLost?.();
        return 'failed';
      }
    },

    setOnline(online) {
      if (state.get().online === online) return;
      publish({ online });
      if (online) {
        for (const r of runtimes.values()) r.failures = 0;
        void (async () => {
          if (options.autosave()) await Promise.all(mine().filter(pending).map((d) => run(d.meta.id)));
          await refresh();
        })();
      }
    },

    resume() {
      publish({ blocked: null, error: null });
      void syncNow();
    },

    forget,
    busy: () => mine().some(pending) || [...runtimes.values()].some((r) => r.inflight),
  };

  function forget(id: string) {
    const r = runtimes.get(id);
    if (!r) return;
    clearTimeout(r.timer);
    runtimes.delete(id);
  }
}
